/**
 * Derives the same acquisition/device context that the client-side Mixpanel
 * SDK (mixpanel-browser) attaches to events, so server-side events the backend
 * sends carry matching values ($utm_source, $os, $device, $browser,
 * Subscription, ...).
 *
 * The OS/browser/device parsing is a faithful port of mixpanel-browser's
 * internal `_.info.os/browser/device` (v2.75.0). Those helpers are not exported
 * by the package, so we mirror them here to guarantee the header values equal
 * what Mixpanel records client-side. Keep in sync if the SDK is upgraded.
 */
import { CLIENT_EXPERIMENTS } from "../experiments/clientExperiments";
import { getStoredSignupContext } from "./signupTracking";

const includes = (str: string, needle: string): boolean =>
  str.indexOf(needle) !== -1;

// Port of mixpanel-browser _.info.os()
export const getMixpanelOs = (): string => {
  const a = navigator.userAgent;
  if (/Windows/i.test(a)) {
    if (/Phone/.test(a) || /WPDesktop/.test(a)) return "Windows Phone";
    return "Windows";
  } else if (/(iPhone|iPad|iPod)/.test(a)) {
    return "iOS";
  } else if (/Android/.test(a)) {
    return "Android";
  } else if (/(BlackBerry|PlayBook|BB10)/i.test(a)) {
    return "BlackBerry";
  } else if (/Mac/i.test(a)) {
    return "Mac OS X";
  } else if (/Linux/.test(a)) {
    return "Linux";
  } else if (/CrOS/.test(a)) {
    return "Chrome OS";
  }
  return "";
};

// Port of mixpanel-browser _.info.browser()
export const getMixpanelBrowser = (): string => {
  const userAgent = navigator.userAgent;
  const vendor = navigator.vendor || ""; // vendor is undefined for at least IE9
  const opera = (window as unknown as { opera?: unknown }).opera;
  if (opera || includes(userAgent, " OPR/")) {
    if (includes(userAgent, "Mini")) return "Opera Mini";
    return "Opera";
  } else if (/(BlackBerry|PlayBook|BB10)/i.test(userAgent)) {
    return "BlackBerry";
  } else if (
    includes(userAgent, "IEMobile") ||
    includes(userAgent, "WPDesktop")
  ) {
    return "Internet Explorer Mobile";
  } else if (includes(userAgent, "SamsungBrowser/")) {
    return "Samsung Internet";
  } else if (includes(userAgent, "Edge") || includes(userAgent, "Edg/")) {
    return "Microsoft Edge";
  } else if (includes(userAgent, "FBIOS")) {
    return "Facebook Mobile";
  } else if (includes(userAgent, "Whale/")) {
    return "Whale Browser";
  } else if (includes(userAgent, "Chrome")) {
    return "Chrome";
  } else if (includes(userAgent, "CriOS")) {
    return "Chrome iOS";
  } else if (includes(userAgent, "UCWEB") || includes(userAgent, "UCBrowser")) {
    return "UC Browser";
  } else if (includes(userAgent, "FxiOS")) {
    return "Firefox iOS";
  } else if (includes(vendor, "Apple")) {
    if (includes(userAgent, "Mobile")) return "Mobile Safari";
    return "Safari";
  } else if (includes(userAgent, "Android")) {
    return "Android Mobile";
  } else if (includes(userAgent, "Konqueror")) {
    return "Konqueror";
  } else if (includes(userAgent, "Firefox")) {
    return "Firefox";
  } else if (includes(userAgent, "MSIE") || includes(userAgent, "Trident/")) {
    return "Internet Explorer";
  } else if (includes(userAgent, "Gecko")) {
    return "Mozilla";
  }
  return "";
};

// Port of mixpanel-browser _.info.device()
export const getMixpanelDevice = (): string => {
  const userAgent = navigator.userAgent;
  if (/Windows Phone/i.test(userAgent) || /WPDesktop/.test(userAgent)) {
    return "Windows Phone";
  } else if (/iPad/.test(userAgent)) {
    return "iPad";
  } else if (/iPod/.test(userAgent)) {
    return "iPod Touch";
  } else if (/iPhone/.test(userAgent)) {
    return "iPhone";
  } else if (/(BlackBerry|PlayBook|BB10)/i.test(userAgent)) {
    return "BlackBerry";
  } else if (/Android/.test(userAgent)) {
    return "Android";
  }
  return "";
};

const INITIAL_CHANNEL_KEY = "initial_channel";

const NOT_SET = "not known";

export type InitialChannel = {
  initial_channel: string;
  initial_creator: string;
  initial_medium: string;
};

const getUrlParam = (name: string): string =>
  new URLSearchParams(window.location.search).get(name) || "";

/**
 * Current/last-touch UTM source — read from the current URL query string, the
 * same value mixpanel-browser attaches to events as `utm_source`.
 */
export const getUtmSource = (): string => getUrlParam("utm_source");

/**
 * Snapshots the first-touch acquisition channel once. Written on the first load
 * ever — even when no params are present (→ "(not set)") — and never overwritten
 * afterwards, so a later UTM/via cannot backfill the initial source. Call once
 * at app startup, before any code that strips URL params (e.g. AppWrapper
 * deleting `via`).
 *
 *   initial_channel = utm_source || pt || "(not set)"
 *   initial_creator = via || "(not set)"
 *   initial_medium  = utm_medium || ("creator" when a via creator is present) || "(not set)"
 */
export const captureInitialChannel = (): void => {
  if (localStorage.getItem(INITIAL_CHANNEL_KEY)) return;

  const via = getUrlParam("via");
  const stored: InitialChannel = {
    initial_channel: getUrlParam("utm_source") || getUrlParam("pt") || NOT_SET,
    initial_creator: via || NOT_SET,
    initial_medium: getUrlParam("utm_medium") || (via ? "creator" : NOT_SET),
  };
  localStorage.setItem(INITIAL_CHANNEL_KEY, JSON.stringify(stored));
  // Fire-and-forget: don't block startup, and swallow errors so a failed
  // attribution ping never surfaces as an unhandled rejection.
};

export const getInitialChannel = (): InitialChannel => {
  try {
    const raw = localStorage.getItem(INITIAL_CHANNEL_KEY);
    if (raw) return JSON.parse(raw) as InitialChannel;
  } catch {
    /* fall through to default */
  }
  return {
    initial_channel: NOT_SET,
    initial_creator: NOT_SET,
    initial_medium: NOT_SET,
  };
};

const isAscii = (value: string): boolean => {
  for (let i = 0; i < value.length; i++) {
    if (value.charCodeAt(i) > 127) return false;
  }
  return true;
};

// Header values must be plain ASCII; URL-encode anything outside it (a UTM
// source could in theory contain non-ASCII characters).
const toAsciiHeaderValue = (value: string): string =>
  isAscii(value) ? value : encodeURIComponent(value);

/**
 * Builds the `x-mp-*` context headers the backend merges into its server-side
 * Mixpanel events. Headers whose value is unknown/empty are omitted entirely
 * (the backend skips absent headers cleanly).
 *
 * Note: distinct_id / $insert_id / time / $ip and Platform are intentionally
 * NOT sent here — the backend stamps those itself.
 */
// OS/device/browser derive from navigator.userAgent, which is immutable for the
// page lifetime. Compute once at module load instead of re-parsing the UA on
// every request (getMixpanelContextHeaders runs in the axios request interceptor).
const OS = getMixpanelOs();
const DEVICE = getMixpanelDevice();
const BROWSER = getMixpanelBrowser();

export const getMixpanelContextHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {};

  // utm_source (SPA navigation), subscription (upgrade) and distinct_id can
  // change during a session, so these stay dynamic per request.
  const utmSource = getUtmSource();
  if (utmSource) headers["x-mp-utm-source"] = toAsciiHeaderValue(utmSource);

  // First-touch acquisition channel (JSON: { utm_source, via }). The backend
  // reads this on /profile and, when firstTimeLogin is true, $set_once's it onto
  // the uuid's Mixpanel profile — blocker-proof, unlike the client people.set_once.
  headers["x-mp-initial-channel"] = toAsciiHeaderValue(
    JSON.stringify(getInitialChannel()),
  );

  // Signup context (set when the login modal opens) forwarded so the backend
  // can fire `user_signed_up` server-side on firstTimeLogin. Present only during
  // the signup flow — cleared after the first /profile fetch.
  const {
    trigger: signupTrigger,
    sourceUrl: signupSourceUrl,
    section: signupSection,
    subsection: signupSubsection,
  } = getStoredSignupContext();
  if (signupTrigger)
    headers["x-mp-signup-trigger"] = toAsciiHeaderValue(signupTrigger);
  if (signupSourceUrl)
    headers["x-mp-signup-source-url"] = toAsciiHeaderValue(signupSourceUrl);
  // section/subsection of the gate the signup started from, so the backend can
  // stamp them on user_signed_up (same dimensions as the section events).
  if (signupSection)
    headers["x-mp-signup-section"] = toAsciiHeaderValue(signupSection);
  if (signupSubsection)
    headers["x-mp-signup-subsection"] = toAsciiHeaderValue(signupSubsection);

  if (OS) headers["x-mp-os"] = OS;
  if (DEVICE) headers["x-mp-device"] = DEVICE;
  if (BROWSER) headers["x-mp-browser"] = BROWSER;

  // Active client-side A/B test buckets, forwarded as one JSON header so the
  // backend can $set_once each on the user's profile at signup and track cohorts
  // forward. Generic over the experiment registry — a new test needs no change
  // here or on the backend. Values read directly (no side effects) since this
  // runs in the axios request interceptor.
  const testGroups: Record<string, string> = {};
  for (const { storageKey, property } of CLIENT_EXPERIMENTS) {
    try {
      const value = localStorage.getItem(storageKey);
      if (value) testGroups[property] = value;
    } catch {
      /* storage disabled — skip */
    }
  }
  if (Object.keys(testGroups).length > 0)
    headers["x-mp-test-groups"] = toAsciiHeaderValue(
      JSON.stringify(testGroups),
    );

  return headers;
};
